<template>
  <div class="banner-admin">
    <div class="toolbar">
      <p class="toolbar-hint">仅上传图片，保存至 <code>uploads/banner</code>，按添加顺序在首页轮播</p>
      <div>
        <input
          ref="uploadInput"
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          class="file-input-hidden"
          @change="onUploadSelected"
        />
        <button type="button" class="btn btn-primary" :disabled="uploading" @click="triggerUpload">
          {{ uploading ? '上传中…' : '+ 上传图片' }}
        </button>
      </div>
    </div>

    <p v-if="error" class="alert alert-error">{{ error }}</p>
    <p v-if="success" class="alert alert-success">{{ success }}</p>

    <div v-if="loading" class="loading">加载中…</div>

    <div v-else-if="!items.length" class="empty-state">暂无轮播图，点击「上传图片」添加</div>

    <ul v-else class="banner-grid">
      <li v-for="(row, index) in items" :key="row.id" class="banner-card">
        <span class="banner-index">{{ index + 1 }}</span>
        <img
          v-if="thumbUrl(row) && !brokenIds[row.id]"
          :src="thumbUrl(row)"
          alt=""
          class="banner-img"
          @error="onThumbError(row.id, $event)"
        />
        <span v-else class="banner-img banner-img--missing">图片加载失败</span>
        <div class="banner-actions">
          <input
            :ref="'replaceInput' + row.id"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            class="file-input-hidden"
            @change="onReplaceSelected(row, $event)"
          />
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            :disabled="replacingId === row.id"
            @click="triggerReplace(row.id)"
          >
            {{ replacingId === row.id ? '更换中…' : '更换' }}
          </button>
          <button type="button" class="btn btn-ghost btn-sm danger" @click="confirmDelete(row)">
            删除
          </button>
        </div>
      </li>
    </ul>

    <p v-if="items.length" class="table-footer">共 {{ items.length }} 张</p>
  </div>
</template>

<script>
import {
  createBanner,
  deleteBanner,
  fetchBanners,
  resolveUploadUrl,
  updateBanner,
  uploadBannerImage
} from '@/api/banners'

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const MAX_BYTES = 5 * 1024 * 1024

export default {
  name: 'AdminBanners',
  data() {
    return {
      items: [],
      loading: false,
      uploading: false,
      replacingId: null,
      brokenIds: {},
      error: '',
      success: ''
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    thumbUrl(row) {
      if (!row) return ''
      return resolveUploadUrl(row.image_url || row.image_path || '')
    },
    onThumbError(id, event) {
      this.brokenIds = { ...this.brokenIds, [id]: true }
      if (event?.target) event.target.style.display = 'none'
    },
    validateFile(file) {
      if (!file) return '请选择图片'
      if (!ALLOWED_TYPES.includes(file.type)) {
        return '仅支持 JPG、PNG、GIF、WEBP 图片'
      }
      if (file.size > MAX_BYTES) {
        return '图片不能超过 5MB'
      }
      return ''
    },
    flashSuccess(message) {
      this.success = message
      setTimeout(() => {
        this.success = ''
      }, 3000)
    },
    triggerUpload() {
      this.$refs.uploadInput?.click()
    },
    triggerReplace(id) {
      const ref = this.$refs[`replaceInput${id}`]
      const el = Array.isArray(ref) ? ref[0] : ref
      el?.click()
    },
    async loadList() {
      this.loading = true
      this.error = ''
      this.brokenIds = {}
      try {
        const res = await fetchBanners()
        this.items = res.data?.items || []
      } catch (err) {
        this.error = err.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async uploadAndCreate(file) {
      const errMsg = this.validateFile(file)
      if (errMsg) {
        this.error = errMsg
        return
      }
      this.uploading = true
      this.error = ''
      try {
        const up = await uploadBannerImage(file)
        const imagePath = up.data?.image_path || ''
        if (!imagePath) throw new Error('上传失败')
        await createBanner({ image_path: imagePath })
        this.flashSuccess('已添加')
        await this.loadList()
      } catch (err) {
        this.error = err.message || '上传失败'
      } finally {
        this.uploading = false
        if (this.$refs.uploadInput) {
          this.$refs.uploadInput.value = ''
        }
      }
    },
    onUploadSelected(event) {
      const file = event.target.files?.[0]
      if (file) this.uploadAndCreate(file)
    },
    async onReplaceSelected(row, event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return

      const errMsg = this.validateFile(file)
      if (errMsg) {
        this.error = errMsg
        return
      }

      this.replacingId = row.id
      this.error = ''
      try {
        const up = await uploadBannerImage(file)
        const imagePath = up.data?.image_path || ''
        if (!imagePath) throw new Error('上传失败')
        await updateBanner(row.id, { image_path: imagePath })
        this.flashSuccess('已更换')
        await this.loadList()
      } catch (err) {
        this.error = err.message || '更换失败'
      } finally {
        this.replacingId = null
      }
    },
    confirmDelete(row) {
      if (!window.confirm('确定删除这张轮播图？')) return
      this.deleteRow(row.id)
    },
    async deleteRow(id) {
      this.error = ''
      try {
        await deleteBanner(id)
        this.flashSuccess('已删除')
        await this.loadList()
      } catch (err) {
        this.error = err.message || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
.banner-admin {
  max-width: 1200px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.toolbar-hint {
  margin: 0;
  font-size: 13px;
  color: #666;
}

.toolbar-hint code {
  font-size: 12px;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
}

.file-input-hidden {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.loading,
.empty-state {
  padding: 48px 24px;
  text-align: center;
  color: #888;
  background: #fff;
  border: 1px dashed #ddd;
  border-radius: 8px;
}

.alert {
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
}

.alert-error {
  background: #fdecea;
  color: #c62828;
}

.alert-success {
  background: #e8f5e9;
  color: #2e7d32;
}

.banner-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.banner-card {
  position: relative;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

.banner-index {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.banner-img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: #f0f0f0;
}

.banner-img--missing {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  font-size: 12px;
}

.banner-actions {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
}

.table-footer {
  margin: 12px 0 0;
  font-size: 12px;
  color: #888;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border-color: #1a1a1a;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f5f5f5;
  border-color: #ddd;
  color: #333;
}

.btn-ghost {
  background: transparent;
  border-color: #ddd;
  color: #444;
}

.btn-ghost.danger {
  color: #c62828;
  border-color: #f5c6cb;
}
</style>
